import { ChangeDetectorRef, Component, Input, OnDestroy, TemplateRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';

import { AUTOCOMPLETER_STATE_SLICE_KEY } from 'SearchApp/ts/features/base-autocomplete/state/autocompleter.reducer';

import { BOTH_DEFAULT_AND_LEGACY_DATA, LEGACY_ONLY_DATA } from 'AccountsApp/ts/constants/user-preferences.constants';
import { LEGACY_DATA_FEATURE } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
import { SEARCH_TYPES_TO_CODES } from 'SearchApp/ts/constants';
import { DataMenuOptionType } from 'AccountsApp/ts/types/user-preference.type';
import { AutocompleteCategory } from 'SearchApp/ts/types/autocomplete-category.type';
import { AutocompleteStore } from 'SearchApp/ts/features/base-autocomplete/state/interfaces/autocomplete-store.interface';
import { AutocompleteResult } from 'SearchApp/ts/interfaces/autocomplete-result.interface';

import { AccountsService } from 'AccountsApp/ts/services/accounts.service';
import { UrlsService } from 'Angular/services/core/urls.service';
import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';

import { SearchResultsBaseDirective } from 'SearchApp/ts/features/base-autocomplete/directives/search-results.directive';

/**
 *
 * List Result Component
 *
 * This component is responsible for:
 *   1. Displaying list results returned from the backend
 *   2. Handling when a result is clicked and hovered
 *   3.- Displaying Search all link and AI link
 *
 * Input Properties:
 *   showMoreResultsUrl: Whether to show the more results header + icon
 *   searchType: The autocompleter from which results are being fetched.
 *
 */

@Component({ standalone: false, selector: 'ycn-list-results', templateUrl: './list-results.template.html' })
export class ListResultsComponent extends SearchResultsBaseDirective implements OnDestroy {
    @Input() showMoreResultsUrl?: boolean;

    @Input() searchType?: string;

    @Input() footerTemplate: TemplateRef<any> | null = null;

    category?: AutocompleteCategory;

    results: AutocompleteResult[] = [];

    selectedIndex: number = -1;

    showResults: boolean = false;

    private storeSubscription: Subscription;

    private dataMenuSetting: DataMenuOptionType = BOTH_DEFAULT_AND_LEGACY_DATA;

    constructor(
        private changeDetector: ChangeDetectorRef,
        urlsService: UrlsService,
        private readonly accountsService: AccountsService,
        private readonly featureService: FeatureService,
        readonly store: Store<{ autocompleter: AutocompleteStore }>
    ) {
        super(urlsService, store);
        this.storeSubscription = this.store.select(AUTOCOMPLETER_STATE_SLICE_KEY).subscribe(this.mapStateToComponent);
        this.accountsService.getUserPreferences().subscribe((userPreferences): void => {
            this.dataMenuSetting = userPreferences.data_menu_setting;
        });
    }

    ngOnDestroy(): void {
        this.storeSubscription.unsubscribe();
    }

    mapStateToComponent = (state: AutocompleteStore): void => {
        const autocompleterState = state[this.autocompleterId];
        if (!autocompleterState) {
            return;
        }
        if (this.searchType) {
            this.category = SEARCH_TYPES_TO_CODES[this.searchType];
        }
        this.inputText = autocompleterState.inputText;
        this.results = autocompleterState.results;
        this.selectedIndex = autocompleterState.selectedIndex;
        this.showResults = autocompleterState.showResults;

        // Disable change detection when results are hidden
        if (this.showResults) {
            this.changeDetector.reattach();
            this.changeDetector.detectChanges();
        } else {
            this.changeDetector.detectChanges();
            this.changeDetector.detach();
        }
    };

    setSelectedIndexOnMouseEnter(result: AutocompleteResult): void {
        // changes selected index on mouseover so highlighted item can be submitted via enter or submit.
        const selectedIndex = this.results.indexOf(result);
        this.setSelectedIndex(selectedIndex);
    }

    /**
     * Removes (Morn) calc/info suffixes from users with Legacy Only data menu setting.
     *
     * This is done only if the user has the LEGACY_DATA_FEATURE in its subscription, and its data menu setting is
     * LEGACY_ONLY_DATA. Since with that combination the user will only see Morningstar calcs, we get rid of the suffix.
     *
     * @param resultName Original name.
     */
    removeMornSuffixForLegacyOnlyUsers(resultName: string): string {
        if (!this.featureService.userHasFeatures([LEGACY_DATA_FEATURE])) return resultName;
        if (this.dataMenuSetting !== LEGACY_ONLY_DATA) return resultName;
        if (!resultName.endsWith(' (Morn)')) return resultName;
        return resultName.slice(0, -7);
    }

    /**
     * Returns the name to display in the results list item. If it should show the raw input, then builds an HTML
     * snippet with the raw input in it.
     *
     * @param result: Name to show for the result item.
     */
    getResultNameText(result: AutocompleteResult): string {
        const resultName = this.removeMornSuffixForLegacyOnlyUsers(result.name);

        if (result.is_raw) {
            return `Press <strong>Enter</strong> to select ${resultName}`;
        }
        return resultName;
    }
}
